import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ProductTileUnitRow as ProductTileUnitRowData } from 'src/lib/product-attributes-pim';
import type { OmnibusPriceDisplay } from '@/lib/omnibus';
import { OmnibusPriceIndication, OmnibusPriceTooltipTrigger } from './omnibus-price-indication';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'src/hooks/use-media-query';
import type { ProductItem } from '../product-tile-custom/product-tile-custom-types';

const previousPriceTransComponents = {
    priceGroup: <span className="inline-flex items-center gap-1" />,
    price: <span className="body-copy-s-bold line-through text-neutral-10" />,
    icon: <OmnibusPriceTooltipTrigger className="text-neutral-10" />,
};

export interface ProductTilePriceLineProps {
    screenReaderLabel: string;
    currentFormatted: string;
    listFormatted?: string | null;
    isOnSale: boolean;
    className?: string;
    invertTextColor?: boolean;
    pricePct: number;
    tileBasketLine: ProductItem | undefined;
    /**
     * Omnibus price disclosure (`OmnibusPriceIndication`). When present it replaces the plain
     * struck-through list price: the previous price it shows is the legal compare-at price.
     */
    omnibus?: ReactNode;
}

export function ProductTilePriceLine({
    screenReaderLabel,
    currentFormatted,
    listFormatted,
    isOnSale,
    className,
    invertTextColor,
    pricePct,
    tileBasketLine,
    omnibus,
}: ProductTilePriceLineProps) {
    const showList = Boolean(isOnSale && listFormatted);
    const { t: tProduct } = useTranslation('product');

    const previousPriceNode = showList && !omnibus && tileBasketLine?.quantity && (
        <div className="flex w-full min-w-0 items-center justify-start gap-1">
            <Trans
                t={tProduct}
                i18nKey="price.previousPrice"
                parent="span"
                className="body-copy-s text-neutral-10 "
                values={{ price: listFormatted ?? '' }}
                components={previousPriceTransComponents}
            />
        </div>
    );

    const visualRow = (
        <div
            aria-hidden={showList ? true : undefined}
            className="flex flex-grow-1 min-w-0 flex-row flex-wrap items-center gap-2">
            {previousPriceNode}

            {isOnSale && pricePct > 0 && (
                <Badge hierarchy="primary" schema="danger" className="shrink-0">
                    -{pricePct}%
                </Badge>
            )}
            <p
                className={cn(
                    'm-0 body-copy-l-bold',
                    isOnSale
                        ? 'text-[var(--status-red-40)]'
                        : invertTextColor
                          ? 'text-neutral-white'
                          : 'text-[var(--neutral-10)]'
                )}>
                {currentFormatted}
            </p>
            {showList && !omnibus ? (
                <span
                    className={cn(
                        'text-sm body-copy-s-bold leading-6 tracking-wide line-through',
                        invertTextColor ? 'text-neutral-white' : 'text-neutral-10'
                    )}>
                    {listFormatted}
                </span>
            ) : null}
        </div>
    );

    const srLabel = (
        <span className="sr-only" aria-atomic>
            {screenReaderLabel}
        </span>
    );

    return (
        <div className={cn('w-full min-w-0', className)} data-testid="product-tile-price-line">
            {showList ? (
                <>
                    {srLabel}
                    {visualRow}
                </>
            ) : (
                <>
                    {visualRow}
                    {srLabel}
                </>
            )}
            {omnibus}
        </div>
    );
}

export interface ProductTileUnitRowProps {
    weightText?: string;
    perUnitText?: string;
    className?: string;
    invertTextColor?: boolean;
}

/** Net weight + dot + price per unit of measure — Body/S, neutral-40. */
export function ProductTileUnitRow({ weightText, perUnitText, className, invertTextColor }: ProductTileUnitRowProps) {
    if (!weightText && !perUnitText) return null;
    return (
        <div
            className={cn(
                'flex w-full min-w-0 flex-wrap items-center gap-2 body-copy-s ',
                invertTextColor ? 'text-neutral-gray-70' : '',
                className
            )}>
            {weightText ? <span className="body-copy-s shrink-0 text-neutral-40">{weightText}</span> : ''}
            {weightText && perUnitText ? (
                <span
                    className="block h-1 w-1 shrink-0 rounded-full bg-current mt-0.5 text-neutral-40"
                    aria-hidden="true"
                />
            ) : null}
            {perUnitText ? <span className="shrink-0 text-neutral-40">{perUnitText}</span> : null}
        </div>
    );
}

export interface ProductTileVerticalBarInfoProps {
    priceBlock: ReactNode;
    unitBlock: ReactNode | null;
    endAdornment: ReactNode | null;
    invertTextColor?: boolean;
}

export function ProductTileVerticalBarInfo({ priceBlock, unitBlock, endAdornment }: ProductTileVerticalBarInfoProps) {
    return (
        <div
            className="content-start flex w-full min-w-0 flex-wrap items-start gap-4"
            data-testid="product-tile-vertical-bar">
            <div className="flex min-h-0 min-w-[110px] max-w-full flex-1 flex-col gap-1">
                {priceBlock}
                <div
                    className="w-full min-w-0 min-h-4 [&:empty]:invisible"
                    aria-hidden={unitBlock == null ? true : undefined}>
                    {unitBlock}
                </div>
            </div>
            {endAdornment != null ? <div className="shrink-0 self-start">{endAdornment}</div> : null}
        </div>
    );
}

export interface ProductTileGenericPriceBarProps {
    t: TFunction<'product'>;
    isOnSale: boolean;
    currentFormatted: string;
    listFormatted?: string | null;
    pricePct: number;
    unitRow: ProductTileUnitRowData;
    perUnitLine: string | null;
    showPrecedingPrice?: boolean;
    isGift?: boolean;
    /** Omnibus disclosure. When it has a previous price or validity, it replaces the static previous-price row. */
    omnibus?: OmnibusPriceDisplay | null;
}

export function ProductTileGenericPriceBar({
    t,
    isOnSale,
    currentFormatted,
    listFormatted,
    pricePct,
    unitRow,
    perUnitLine,
    showPrecedingPrice = true,
    isGift = false,
    omnibus = null,
}: ProductTileGenericPriceBarProps) {
    const isMobile = useMediaQuery('(max-width: 400px)');
    const showList = Boolean(isOnSale && listFormatted && showPrecedingPrice);
    const showOmnibus = Boolean(
        showPrecedingPrice && omnibus && (omnibus.priorFormatted || omnibus.validFrom || omnibus.validTo)
    );
    const unitNode = (unitRow.weightText || perUnitLine) && (
        <ProductTileUnitRow weightText={unitRow.weightText} perUnitText={perUnitLine ?? undefined} />
    );

    const previousPriceNode = showOmnibus ? (
        <OmnibusPriceIndication indication={omnibus} />
    ) : (
        showList && (
            <div className="flex w-full min-w-0 items-center justify-start gap-1">
                <Trans
                    t={t}
                    i18nKey="price.previousPrice"
                    parent="span"
                    className="body-copy-s text-neutral-10"
                    values={{ price: listFormatted ?? '' }}
                    components={previousPriceTransComponents}
                />
            </div>
        )
    );

    const unitWrapperNode = (
        <div
            className={cn(
                'min-w-0 min-h-4 [&:empty]:invisible text-neutral-40',
                isMobile ? 'w-full flex-1 flex flex-col gap-1 min-h-0 max-w-full' : 'w-full'
            )}
            aria-hidden={unitNode == null ? true : undefined}>
            {unitNode}
        </div>
    );

    const priceBadgeNode = (
        <div className={cn('flex shrink-0 gap-1', isMobile ? 'flex-col items-end' : 'items-center')}>
            {isGift ? (
                <Badge
                    hierarchy="primary"
                    schema="danger"
                    className="shrink-0"
                    role="status"
                    aria-label={t('giftBadgeAriaLabel')}>
                    {t('giftBadge')}
                </Badge>
            ) : (
                <>
                    {isOnSale && pricePct > 0 && (
                        <Badge hierarchy="primary" schema="danger" className="shrink-0">
                            -{pricePct}%
                        </Badge>
                    )}
                    <p
                        className={cn(
                            'm-0 text-right body-copy-l-bold whitespace-nowrap text-neutral-10',
                            isOnSale && 'text-status-red-40'
                        )}>
                        {currentFormatted}
                    </p>
                </>
            )}
        </div>
    );

    return (
        <div
            className={cn('flex w-full min-w-0', isMobile ? 'flex-wrap items-end gap-3' : 'flex-col')}
            data-testid="cart-product-line-price-bar">
            {!isMobile && (
                <div className="flex w-full min-w-0 justify-between gap-3">
                    <div>{previousPriceNode}</div>
                    <div>{priceBadgeNode}</div>
                </div>
            )}

            {isMobile && previousPriceNode}

            {unitWrapperNode}

            {isMobile && priceBadgeNode}
        </div>
    );
}
